import { useState } from "react";
import { addDays, todayIn, type IsoDate } from "../../../shared/dates";
import { daysUntil, sleepsLabel, upcomingCountdowns, type Countdown } from "../../../shared/home";
import { useAppUi } from "../../app/uiContext";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { Icon } from "../../ui/Icon";
import { Confirm, Sheet } from "../../ui/Sheet";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { useNow } from "../../ui/time";
import { fmtDate } from "../calendar/calendarModel";
import type { WidgetDefinition, WidgetProps } from "../types";
import { remPx } from "../sky/common";
import { WidgetHeader } from "../WidgetHeader";
import { useCountdownActions, useCountdowns } from "./api";
import "./home.css";

interface CountdownsConfig {
  max: number;
}

const EMOJI = ["🎉", "🎂", "🎄", "🎃", "✈️", "🏖️", "⛺", "🎿", "🏫", "🎁", "🐣", "🎆", "⚽", "🎭", "🦃", "❤️"];

/** "Sleeps until": the days left to the things the family is looking forward to. */
function Countdowns({ config, size, editing, fullscreen, openFullscreen }: WidgetProps<CountdownsConfig>) {
  const { timeZone } = useHousehold();
  const now = useNow("minute");
  const today = todayIn(timeZone, now);
  const q = useCountdowns();
  const state = dataStateOf(q, 60 * 60_000);
  const [open, setOpen] = useState<Countdown | "new" | null>(null);
  const list = upcomingCountdowns(q.data ?? [], today).slice(0, fullscreen ? 40 : config.max);
  const [hero, ...rest] = list;
  const showHero = hero && size.height >= remPx() * 14.5;

  return (
    <div className="home-w countdowns">
      <WidgetHeader
        title="Sleeps until…"
        onTitle={fullscreen ? undefined : openFullscreen}
        actions={
          <button className="icon-btn icon-btn-accent" onClick={() => setOpen("new")} aria-label="Add a countdown" disabled={editing}>
            <Icon name="plus" />
          </button>
        }
      />
      {!q.data ? (
        state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="hourglass" title="Countdowns unavailable" detail={q.error?.message} />
      ) : !hero ? (
        <EmptyState
          icon="hourglass"
          title="Nothing to count down to"
          detail="Add a birthday, a trip or the last day of school."
          action={
            <button className="btn btn-sm btn-primary" onClick={() => setOpen("new")} disabled={editing}>
              Add a countdown
            </button>
          }
        />
      ) : (
        <>
          {showHero && (
            <button className="cd-hero" onClick={() => setOpen(hero)} disabled={editing}>
              <span className="cd-emoji">{hero.emoji}</span>
              <span className="cd-hero-text">
                <strong className="cd-big">{daysUntil(hero.date, today) === 0 ? "Today!" : daysUntil(hero.date, today)}</strong>
                {daysUntil(hero.date, today) > 0 && <span className="cd-unit">{daysUntil(hero.date, today) === 1 ? "sleep until" : "sleeps until"}</span>}
                <span className="cd-title">{hero.title}</span>
                <span className="muted">{fmtDate(hero.date, { weekday: "long", month: "long", day: "numeric" })}</span>
              </span>
            </button>
          )}
          <ul className="cd-list">
            {(showHero ? rest : list).map((c) => (
              <li key={c.id}>
                <button className="cd-row" onClick={() => setOpen(c)} disabled={editing}>
                  <span className="cd-emoji-sm">{c.emoji}</span>
                  <span className="cd-row-main">
                    <strong>{c.title}</strong>
                    <span className="muted">{fmtDate(c.date, { weekday: "short", month: "short", day: "numeric" })}</span>
                  </span>
                  <span className={`cd-sleeps ${daysUntil(c.date, today) <= 1 ? "is-soon" : ""}`}>{sleepsLabel(c.date, today)}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
      {open && <CountdownEditor countdown={open === "new" ? null : open} today={today} onClose={() => setOpen(null)} />}
    </div>
  );
}

function CountdownEditor({ countdown, today, onClose }: { countdown: Countdown | null; today: IsoDate; onClose: () => void }) {
  const actions = useCountdownActions();
  const { toast } = useAppUi();
  const [title, setTitle] = useState(countdown?.title ?? "");
  const [date, setDate] = useState<IsoDate>(countdown?.date ?? addDays(today, 7));
  const [emoji, setEmoji] = useState(countdown?.emoji ?? "🎉");
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const save = () => actions.save.mutate({ id: countdown?.id ?? null, input: { title, date, emoji } }, { onSuccess: onClose, onError: (e) => setError((e as Error).message) });

  return (
    <Sheet
      title={countdown ? "Edit countdown" : "New countdown"}
      onClose={onClose}
      footer={
        <>
          {countdown && (
            <button className="btn btn-danger-ghost" onClick={() => setConfirm(true)}>
              <Icon name="trash" size="1.1rem" /> Delete
            </button>
          )}
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={actions.save.isPending || !title.trim()}>
            {actions.save.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="form">
        <label className="field">
          <span>Counting down to</span>
          <input className="input input-lg" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={60} placeholder="e.g. Grandma’s visit" autoFocus />
        </label>
        <label className="field">
          <span>Date</span>
          <input className="input input-date" type="date" value={date} min={today} onChange={(e) => e.target.value && setDate(e.target.value)} />
          <small className="muted">{sleepsLabel(date, today)} from today</small>
        </label>
        <div className="field">
          <span>Picture</span>
          <div className="chip-row wrap">
            {EMOJI.map((e) => (
              <button key={e} className={`chip round cd-pick ${emoji === e ? "on" : ""}`} onClick={() => setEmoji(e)} aria-pressed={emoji === e}>
                {e}
              </button>
            ))}
          </div>
        </div>
        {error && <p className="error-text">{error}</p>}
      </div>
      {confirm && countdown && (
        <Confirm
          title="Delete countdown?"
          message={`“${countdown.title}” will be removed for everyone.`}
          confirmLabel="Delete"
          danger
          onCancel={() => setConfirm(false)}
          onConfirm={() => {
            onClose();
            actions.remove.mutate(countdown, { onError: (e) => toast(`Couldn’t delete: ${(e as Error).message}`) });
          }}
        />
      )}
    </Sheet>
  );
}

export const countdownsWidget: WidgetDefinition<CountdownsConfig> = {
  type: "countdowns",
  version: 1,
  name: "Countdowns",
  description: "“Sleeps until” birthdays, trips and holidays, with the next one big.",
  icon: "hourglass",
  category: "household",
  defaultSize: { w: 4, h: 4 },
  minSize: { w: 2, h: 2 },
  stackHeight: 340,
  defaults: { max: 6 },
  settings: [{ key: "max", label: "Countdowns shown", type: "number", min: 1, max: 12 }],
  supportsFullscreen: true,
  Component: Countdowns,
};
